import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { EAttributeType } from '../@types/AttributeTypes';
import AttributeModel, { IAttribute } from '../models/standard/AttributeModel';
import { usePlan } from '../provider/PlanProvider';
import { AttributeSelectionInput, mergeAttributesById, resolveAttributeSelection } from '../utils/attributes/attributeCreationUtils';
import filesApiService from '../utils/filesApiService';

export interface IAttributeActions {
  /**
   * Updates the value of a specific attribute.
   * @param attrId - The ID of the attribute to update.
   * @param value - The new value for the attribute.
   */
  handleAttributeValueChange: (attrId: number, value: string) => void;

  /**
   * Deletes a specific attribute and updates the predecessor chain.
   * @param attrId - The ID of the attribute to delete.
   */
  handleDeleteAttribute: (attrId: number) => void;

  /**
   * Toggles the visibility of an attribute's input field.
   * @param attrId - The ID of the attribute to toggle.
   */
  toggleInputField: (attrId: number) => void;

  /**
   * Handles the selection of a new attribute from the autocomplete.
   * @param sel - The selected attribute or null.
   */
  handleAttributeSelection: (sel: AttributeSelectionInput) => Promise<void>;

  /**
   * Sorts attributes based on their predecessorId chain.
   * @param attrs - The list of attributes to sort.
   * @returns The sorted list of attributes.
   */
  getSortedAttributes: (attrs: AttributeModel[]) => AttributeModel[];
}

/**
 * Custom hook for managing attribute-related actions.
 * @param attributes - The current list of attributes.
 * @param setAttributes - State setter for attributes.
 * @param setAttributeValues - State setter for attribute values.
 * @param setShowInputFields - State setter for input field visibility.
 * @param setAttrSelected - State setter for the selected attribute.
 * @param setAttrInputValue - State setter for the autocomplete input value.
 * @param updateAttributes - Callback to update attributes in the global state.
 * @returns An object containing attribute action handlers.
 */
export const useAttributeActions = (
  attributes: AttributeModel[],
  setAttributes: React.Dispatch<React.SetStateAction<AttributeModel[]>>,
  setAttributeValues: React.Dispatch<React.SetStateAction<Record<number, string>>>,
  setShowInputFields: React.Dispatch<React.SetStateAction<Record<number, boolean>>>,
  setAttrSelected: React.Dispatch<React.SetStateAction<IAttribute | null>>,
  setAttrInputValue: React.Dispatch<React.SetStateAction<string>>,
  updateAttributes: (attributes: AttributeModel[]) => void,
  attributeCatalog?: IAttribute[],
  setAttributeCatalog?: React.Dispatch<React.SetStateAction<IAttribute[]>>,
  setAttributeError?: React.Dispatch<React.SetStateAction<string | null>>,
): IAttributeActions => {
  const intl = useIntl();
  const { planModel } = usePlan();
  const regionCode = planModel?.region ? String(planModel.region).toUpperCase() : 'DE';

  const refreshAttributeCatalog = useCallback(
    (createdAttribute: IAttribute) => {
      if (!setAttributeCatalog) return;

      filesApiService
        .getAttributes(regionCode)
        .then((refreshedAttributes) => {
          setAttributeCatalog((prev) => mergeAttributesById(refreshedAttributes, prev, [createdAttribute]));
        })
        .catch(() => undefined);
    },
    [regionCode, setAttributeCatalog],
  );
  
  // Sorting logic (unchanged, but ensures objects are preserved)
  const getSortedAttributes = useCallback((attrs: AttributeModel[]) => {
    const map = new Map(attrs.map((a) => [a.id, a]));
    const sorted: AttributeModel[] = [];
    let curr = attrs.find((a) => a.predecessorId === null)?.id ?? null;
    
    while (curr !== null && map.has(curr)) {
      const a = map.get(curr)!;
      sorted.push(a);
      curr = attrs.find((n) => n.predecessorId === a.id)?.id || null;
    }
    
    // Add any orphans that broke the chain
    attrs.forEach((a) => { if (!sorted.includes(a)) sorted.push(a); });
    return sorted;
  }, []);

  const handleAttributeValueChange = useCallback((attrId: number, val: string) => {
    setAttributeValues((prev) => ({ ...prev, [attrId]: val }));
    setShowInputFields((prev) => ({ ...prev, [attrId]: true }));
    
    const updated = attributes.map((a) => 
      a.id === attrId
        ? new AttributeModel(
            a.id, 
            a.key, 
            val, 
            a.predecessorId, 
            a.type, 
            a.unit, 
            a.hidden, 
            a.forced,
            // Pass all extended properties to prevent data loss
            a.ownerId,
            a.userId,
            a.publicFlag,
            a.localization,
            a.defaults,
            a.validation,
            a.unitFamily
          )
        : a
    );

    const sorted = getSortedAttributes(updated);
    setAttributes(updated);
    updateAttributes(sorted);
  }, [attributes, getSortedAttributes, setAttributes, setAttributeValues, setShowInputFields, updateAttributes]);

  const handleDeleteAttribute = useCallback((attrId: number) => {
    const toDel = attributes.find((a) => a.id === attrId)!;
    
    let remaining = attributes
      .filter((a) => a.id !== attrId)
      .map((a) => 
        a.predecessorId === attrId
          ? new AttributeModel(
              a.id, 
              a.key, 
              a.value, 
              toDel.predecessorId, // Patch predecessor chain
              a.type, 
              a.unit, 
              a.hidden, 
              a.forced,
              // Preserve extended properties
              a.ownerId,
              a.userId,
              a.publicFlag,
              a.localization,
              a.defaults,
              a.validation,
              a.unitFamily
            )
          : a
      );

    const sortedRemaining = getSortedAttributes(remaining);
    
    // Handle case where we deleted the first item (head)
    if (sortedRemaining.length && toDel.predecessorId === null) {
      remaining = remaining.map((a) =>
        a.id === sortedRemaining[0].id
          ? new AttributeModel(
              a.id, 
              a.key, 
              a.value, 
              null, // New head has no predecessor
              a.type, 
              a.unit, 
              a.hidden, 
              a.forced,
              // Preserve extended properties
              a.ownerId,
              a.userId,
              a.publicFlag,
              a.localization,
              a.defaults,
              a.validation,
              a.unitFamily
            )
          : a
      );
    }

    setAttributes(remaining);
    updateAttributes(remaining);
    setAttributeValues((prev) => { const c = { ...prev }; delete c[attrId]; return c; });
    setShowInputFields((prev) => { const c = { ...prev }; delete c[attrId]; return c; });
  }, [attributes, getSortedAttributes, setAttributes, setAttributeValues, setShowInputFields, updateAttributes]);

  const toggleInputField = useCallback((attrId: number) => {
    setShowInputFields((prev) => ({ ...prev, [attrId]: !prev[attrId] }));
  }, [setShowInputFields]);

  const handleAttributeSelection = useCallback(async (sel: AttributeSelectionInput) => {
    setAttributeError?.(null);

    let resolvedAttribute: IAttribute | null = null;

    try {
      resolvedAttribute = await resolveAttributeSelection({
        attributeCatalog: attributeCatalog || [],
        currentAttributes: attributes,
        locale: intl.locale,
        onAttributeCreated: (createdAttribute) => {
          setAttributeCatalog?.((prev) => mergeAttributesById(prev, [createdAttribute]));
          refreshAttributeCatalog(createdAttribute);
        },
        selection: sel,
      });
    } catch (error) {
      setAttributeError?.(
        intl.formatMessage({
          id: 'AttributeAutocomplete.createAttributeError',
          defaultMessage: 'Attribute could not be created. Please make sure you are logged in.',
        }),
      );
      return;
    }

    if (!resolvedAttribute) return;

    const existing = attributes;
    if (existing.some((attribute) => attribute.id === resolvedAttribute.id)) {
      setAttrSelected(null);
      setAttrInputValue('');
      return;
    }

    const pred = existing.length ? existing[existing.length - 1].id : null;
    
    // Instantiate new model with ALL properties from the selected object
    const newAttr = new AttributeModel(
      resolvedAttribute.id, 
      resolvedAttribute.key, 
      undefined, 
      pred, 
      resolvedAttribute.type as EAttributeType, 
      resolvedAttribute.unit, 
      resolvedAttribute.hidden, 
      resolvedAttribute.forced,
      // Extended properties coming from the autocomplete object
      resolvedAttribute.ownerId,
      resolvedAttribute.userId,
      resolvedAttribute.publicFlag,
      resolvedAttribute.localization,
      resolvedAttribute.defaults,
      resolvedAttribute.validation,
      resolvedAttribute.unitFamily
    );

    const updated = [...existing, newAttr];
    const sorted = getSortedAttributes(updated);
    
    setAttributes(sorted);
    updateAttributes(sorted);
    setAttrSelected(null);
    setAttrInputValue('');
  }, [
    attributeCatalog,
    attributes,
    getSortedAttributes,
    intl,
    setAttributeCatalog,
    setAttributeError,
    setAttributes,
    setAttrInputValue,
    setAttrSelected,
    updateAttributes,
    refreshAttributeCatalog,
  ]);

  return {
    handleAttributeValueChange,
    handleDeleteAttribute,
    toggleInputField,
    handleAttributeSelection,
    getSortedAttributes,
  };
};
